import React from 'react';
import { X, Printer } from 'lucide-react';

const MaterialDeliveryPreviewModal = ({ isOpen, onClose, delivery }) => {
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

  if (!isOpen || !delivery) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 screen-only">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Delivery Details</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => window.print()}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Printer className="h-5 w-5 mr-2" />
              Print
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Header Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Delivery Number</p>
                <p className="text-lg font-medium text-gray-900">#{delivery.deliveryNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="text-lg font-medium text-gray-900">{formatDate(delivery.date)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Source Warehouse</p>
                <p className="text-lg font-medium text-gray-900">{delivery.sourceWarehouse.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Destination</p>
                {delivery.isCustomerDelivery ? (
                    <div>
                        <div className="text-lg font-medium text-gray-900">
                        {delivery.customerId?.name || 'Unknown Customer'}
                        </div>
                        {delivery.customerId?.isCompany && delivery.customerId?.taxId && (
                        <div>
                            <div className="text-xs text-gray-500">Tax ID: {delivery.customerId.taxId}</div>
                        </div>
                        )}
                        {delivery.customerId?.address && (
                        <p className="text-sm text-gray-500">
                            {delivery.customerId.address.street && `${delivery.customerId.address.street}, `}
                            {delivery.customerId.address.city && delivery.customerId.address.postalCode && 
                            `${delivery.customerId.address.postalCode} ${delivery.customerId.address.city}`}
                        </p>
                        )}
                    </div>
                    ) : (
                    <p className="text-lg font-medium text-gray-900">
                        {delivery.targetWarehouse?.name || 'Unknown Warehouse'}
                    </p>
                    )}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Average Price
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {delivery.items.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.item.name}</div>
                      <div className="text-sm text-gray-500">{item.item.amountType}</div>
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {item.quantity} {item.item.amountType}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        ${item.averagePrice.toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ${(item.quantity * item.averagePrice).toFixed(2)}
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gray-50">
                  <td colSpan="3" className="px-6 py-4 text-right">
                    <div className="text-sm font-medium text-gray-500">Total Value:</div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="text-base font-semibold text-gray-900">
                      ${delivery.items.reduce((sum, item) => sum + (item.quantity * item.averagePrice), 0).toFixed(2)}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Additional Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Created By</p>
                <p className="text-sm font-medium text-gray-900">{delivery.createdBy?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created At</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(delivery.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialDeliveryPreviewModal;